/**
 * CasinoAgentLowBanner — persistent red banner that shows on every admin page
 * when the MajikBonus agent score has dipped below the configured threshold.
 *
 * Renders nothing when balance is healthy. Polls /admin/casino-agent-status
 * every 60s — cheap cached read, no upstream Majik call from the UI.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, X, Settings as SettingsIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DISMISS_KEY = 'casinoAgentLowBanner.dismissedAt';
const DISMISS_WINDOW_MS = 60 * 60 * 1000; // 1h grace after manual dismiss

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CasinoAgentLowBanner = () => {
    const [status, setStatus] = useState(null);
    const [dismissedUntil, setDismissedUntil] = useState(() => {
        try {
            const v = Number(localStorage.getItem(DISMISS_KEY) || 0);
            return v > Date.now() ? v : 0;
        } catch { return 0; }
    });

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await axios.get(`${API}/admin/casino-agent-status`, { withCredentials: true });
                if (!cancelled) setStatus(data);
            } catch { /* silent — banner just won't show */ }
        };
        load();
        const id = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    const isLow = !!(status?.active);
    const isDismissed = dismissedUntil > Date.now();

    if (!isLow || isDismissed) return null;

    const dismiss = () => {
        const until = Date.now() + DISMISS_WINDOW_MS;
        try { localStorage.setItem(DISMISS_KEY, String(until)); } catch { /* ignore */ }
        setDismissedUntil(until);
    };

    return (
        <div
            className="bg-gradient-to-r from-red-700/95 via-red-600/95 to-red-700/95 border-y-2 border-red-400 px-4 py-2.5 shadow-lg"
            data-testid="casino-agent-low-banner"
        >
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    <AlertTriangle className="w-5 h-5 text-yellow-200 flex-shrink-0 animate-pulse" />
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-white">
                            Casino agent balance LOW · {fmt(status.score)}
                            <span className="text-yellow-100/80 font-normal text-xs ml-2">
                                threshold {fmt(status.threshold)} · top up before player topups start failing
                            </span>
                        </p>
                        {status.last_triggered_at && (
                            <p className="text-[10px] text-red-100/70">
                                First detected {new Date(status.last_triggered_at).toLocaleString()}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <a
                        href="/admin#casino"
                        className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded bg-yellow-300 text-red-900 hover:bg-yellow-200 transition flex items-center gap-1.5"
                        data-testid="casino-agent-low-banner-topup"
                    >
                        <SettingsIcon className="w-3 h-3" />
                        Top up agent
                    </a>
                    <button
                        type="button"
                        onClick={dismiss}
                        title="Hide banner for 1 hour"
                        className="text-red-100 hover:text-white p-1 rounded transition"
                        data-testid="casino-agent-low-banner-dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CasinoAgentLowBanner;
